import { Directive, ElementRef, OnDestroy, OnInit, effect, inject, input } from '@angular/core';

const MAX_LINES = 10;

/**
 * Keeps a textarea as tall as its text, up to 10 lines and scrolling after that: when it shows a
 * saved value, when the text changes from outside (a variable inserted), when it is typed in, and
 * when it gets another width or comes into view (a section unfolded, the panel dragged wider).
 * Bind the text: `[appAutoGrow]="value"`.
 */
@Directive({
  selector: 'textarea[appAutoGrow]',
  host: {'(input)': 'resize()'},
})
export class AutoGrowTextareaDirective implements OnInit, OnDestroy {
  readonly appAutoGrow = input<string | null | undefined>(null);

  private element = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
  private observer: ResizeObserver | null = null;
  private frame: number | null = null;
  private width = -1;

  constructor() {
    effect(() => {
      this.appAutoGrow();
      this.scheduleResize();
    });
  }

  ngOnInit(): void {
    // Only a new width: this directive's own height change would otherwise notify again, and a
    // change made inside the observer's callback is reported as a window error.
    this.observer = new ResizeObserver(() => {
      const width = this.element.nativeElement.clientWidth;
      if (width !== this.width) {
        this.width = width;
        this.scheduleResize();
      }
    });
    this.observer.observe(this.element.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }
  }

  resize(): void {
    const textarea = this.element.nativeElement;
    textarea.style.height = 'auto';
    // Not on screen (a folded section) it has no height to measure; the observer sees it open.
    if (!textarea.scrollHeight) {
      return;
    }
    const border = textarea.offsetHeight - textarea.clientHeight;
    const maxHeight = parseFloat(getComputedStyle(textarea).lineHeight) * MAX_LINES + border;
    const height = textarea.scrollHeight + border;
    textarea.style.height = Math.min(height, maxHeight) + 'px';
    textarea.style.overflowY = height > maxHeight ? 'auto' : 'hidden';
  }

  // On the next frame: ngModel writes a new value to the textarea after the input binding changes.
  private scheduleResize(): void {
    if (this.frame !== null) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.resize();
    });
  }
}
